import type { AxiosError, AxiosRequestHeaders } from 'axios';
import axios from 'axios';
import applyCaseMiddleware from 'axios-case-converter';

import { getToken } from './getToken';
import { ApiError } from '~/shared/models/ApiError';
import type { FetcherOptions } from '~/shared/types/FetcherOptions';

export const client = applyCaseMiddleware(axios.create(), {
  // preserve keys in translations that represent locales, e.g. en-GB, ka-GE
  preservedKeys: (input) => /^[A-Za-z]{2}-[A-Za-z]{2}$/.test(input),
  caseFunctions: {
    // overrides built-in snake function to keep the keys of outcoming data in camelCase
    snake: (input) => input.replace(/[^\dA-Za-z]+(.)/g, (_, char: string) => char.toUpperCase()),
  },
});

/**
 * Fetcher function for SWR hook.
 *
 * Use it only for `GET` request.
 * @param {string} url - endpoint URL
 * @param {FetcherOptions} options - options passed to axios
 */
export async function fetcher<T>(url: string, options: FetcherOptions): Promise<T> {
  const { identityUrl, tenant, ...config } = options;
  const token = await getToken({
    tenant,
    identityUrl,
  });
  const headers: AxiosRequestHeaders = {
    ...config.headers,
    Authorization: token.toString(),
    'x-tenant-context': tenant,
  };

  const { data } = await client
    .get<T>(url, {
      ...config,
      headers,
    })
    .then((response) => response)
    .catch((error: AxiosError<T>) => {
      throw new ApiError(error.message, error);
    });

  return data;
}
